import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe2,
  Loader2,
  ArrowLeft,
  ShieldAlert,
  KeyRound,
  User,
  CheckCircle2,
  Copy,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import PasswordInput from "@/components/ui/PasswordInput";

/**
 * Emergency recovery for a MASTER_ADMIN who lost email access.
 *
 * The system operator (the person with backend/.env access) sets
 * ADMIN_RECOVERY_SECRET. Anyone with that secret + the admin username can
 * generate a ONE-TIME reset link here, then use the standard /reset-password
 * flow to choose a new password. Every attempt is audit-logged.
 */
export default function AdminRecovery() {
  const navigate = useNavigate();
  const { brand } = useBranding();
  const [username, setUsername] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // {reset_url, expires_at, username}

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/auth/admin-emergency-recovery/init", {
        recovery_secret: secret,
        username: username.trim(),
      });
      setResult(r.data);
      toast.success("One-time recovery link generated.");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Recovery failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.reset_url) return;
    try {
      await navigator.clipboard.writeText(result.reset_url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy — please select the link manually");
    }
  };

  const openLink = () => {
    if (!result?.reset_url) return;
    try {
      const u = new URL(result.reset_url);
      navigate(`${u.pathname}${u.search}`);
    } catch {
      window.location.href = result.reset_url;
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-10 bg-[#F8FAFC]"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      data-testid="admin-recovery-page"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-10 h-10 rounded-lg bg-[#0F62FE] text-white flex items-center justify-center overflow-hidden">
            {brand.brand_logo_url ? (
              <img src={brand.brand_logo_url} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <Globe2 className="w-6 h-6" strokeWidth={2.2} />
            )}
          </div>
          <div className="font-bold text-xl">{brand.brand_name}</div>
        </div>

        <button
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900 mb-4"
          data-testid="admin-recovery-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </button>

        <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
          <ShieldAlert className="w-3.5 h-3.5" /> Break-glass recovery
        </div>
        <h2
          className="text-3xl font-bold tracking-tight text-slate-900 mt-3"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Master Admin recovery
        </h2>
        <p className="text-slate-500 mt-2 text-sm">
          For emergencies only. If the Master Admin has lost access to their email,
          the system operator can generate a one-time password reset link using
          the <span className="font-semibold text-slate-800">ADMIN_RECOVERY_SECRET</span>{" "}
          configured in the server environment.
        </p>

        {!result ? (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-recovery-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Master Admin username
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="admin-recovery-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  required
                  autoFocus
                  className="flex h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                  placeholder="admin"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Recovery secret
              </label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                <PasswordInput
                  data-testid="admin-recovery-secret"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="off"
                  required
                  className="flex h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                  placeholder="ADMIN_RECOVERY_SECRET from server .env"
                />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                This value must match the one set in{" "}
                <code className="bg-slate-100 px-1 rounded">backend/.env</code>.
                Every attempt is written to the Audit Log.
              </div>
            </div>

            {err && (
              <div
                data-testid="admin-recovery-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !username.trim() || !secret}
              data-testid="admin-recovery-submit"
              className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] disabled:opacity-60 transition-colors"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Generate one-time reset link
            </button>

            <p className="text-[11px] text-slate-500 text-center">
              Restricted to <span className="font-semibold text-slate-700">MASTER_ADMIN</span> accounts.
              Wrong secrets are throttled and audit-logged.
            </p>
          </form>
        ) : (
          <div className="mt-6" data-testid="admin-recovery-result">
            <div className="text-center pb-4">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
              <h3
                className="text-2xl font-bold text-slate-900 mt-3"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Recovery link generated
              </h3>
              <p className="text-slate-500 mt-2 text-sm">
                A one-time reset link has been created for{" "}
                <span className="font-semibold text-slate-800">{result.username}</span>.
                It expires in 30 minutes and can only be used once. All existing
                sessions will be revoked after the password is changed.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                One-time reset link
              </div>
              <div
                data-testid="admin-recovery-link"
                className="text-xs text-[#0F62FE] break-all font-mono select-all"
              >
                {result.reset_url}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={copyLink}
                data-testid="admin-recovery-copy"
                className="h-11 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
              >
                <Copy className="w-4 h-4" /> Copy link
              </button>
              <button
                onClick={openLink}
                data-testid="admin-recovery-open"
                className="h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6] transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Open reset page
              </button>
            </div>

            <div className="mt-6 text-[11px] text-slate-500 text-center">
              Expires at{" "}
              {result.expires_at
                ? new Date(result.expires_at).toLocaleString("en-US")
                : "in 30 minutes"}
              . Save or open the link now — it cannot be shown again.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
